import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Input from 'common-ui/input';
import CustomSelectComponent from 'common-ui/select/Select';
import { getAssigned, getWorkflowStatuses } from 'features/newsales/newsalesActions';
import { selectAssigned, selectWorkflowStatuses } from 'features/newsales/newsalesSlice';
import MyPhoneInput from 'pages/components/myPhoneInput';

import { useLeadForm } from '../LeadFormContext';
import { CONTACT_INFO_DEFAULT_VALUES } from '../salesNewConstants';
import '../salesNewConstants';
import {
  Container,
  ErrorMessage,
  InputGroup,
  InputWrapper,
  Label,
  MainInfoContent,
  Title,
} from './ContactInfo.styles';

const ContactInfo = ({ onValidationChange, shouldValidate }) => {
  const assigned = useSelector(selectAssigned);
  const dispatch = useDispatch();
  const { formData, updateFormData } = useLeadForm();
  const workflowStatuses = useSelector(selectWorkflowStatuses);
  const { control, watch, reset } = useForm({
    defaultValues: CONTACT_INFO_DEFAULT_VALUES,
  });

  const [errors, setErrors] = useState({
    phone: '',
    status: '',
    firstName: '',
    lastName: '',
  });
  const [touched, setTouched] = useState({
    phone: false,
    status: false,
    firstName: false,
    lastName: false,
  });

  const assignedList = Array.isArray(assigned) ? assigned : assigned?.users || [];

  const formValues = watch();

  useEffect(() => {
    if (!workflowStatuses?.length) return;

    const selectedStatus = workflowStatuses.find((s) => s.id === formData.workflowStatusId);

    reset({
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      patronymic: formData.patronymic || '',
      phone: { value: formData.phoneNumber || '' },
      status: selectedStatus ? { value: selectedStatus.id, label: selectedStatus.name } : null,
      assignee: formData.assigneeUuid || null,
    });
  }, [workflowStatuses]);
  useEffect(() => {
    dispatch(getWorkflowStatuses({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAssigned());
  }, [dispatch]);

  const statusOptions = workflowStatuses
    ? workflowStatuses.map((status) => ({
        value: status.id,
        label: status.name,
      }))
    : [];

  useEffect(() => {
    updateFormData({
      phoneNumber: formValues.phone?.value || '',
      firstName: formValues.firstName || '',
      lastName: formValues.lastName || '',
      patronymic: formValues.patronymic || '',
      workflowStatusId: formValues.status?.value || null,
      assigneeUuid: formValues.assignee || null,
    });
  }, [
    formValues.phone?.value,
    formValues.firstName,
    formValues.lastName,
    formValues.patronymic,
    formValues.status?.value,
    formValues.assignee,
  ]);
  const validateForm = () => {
    const newErrors = {
      phone: '',
      status: '',
      firstName: '',
      lastName: '',
    };

    const phoneValue = formValues.phone?.value || '';
    if (!phoneValue || phoneValue.length < 10) {
      newErrors.phone = 'Valid phone number is required';
    }

    if (!formValues.status) {
      newErrors.status = 'Status is required';
    }

    if (!formValues.firstName || formValues.firstName.trim() === '') {
      newErrors.firstName = 'First name is required';
    }

    if (!formValues.lastName || formValues.lastName.trim() === '') {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);

    const isValid =
      phoneValue &&
      phoneValue.length >= 10 &&
      formValues.status &&
      formValues.firstName &&
      formValues.firstName.trim() !== '' &&
      formValues.lastName &&
      formValues.lastName.trim() !== '';

    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm();
    onValidationChange?.(isValid);
  }, [
    formValues.phone?.value,
    formValues.firstName,
    formValues.lastName,
    formValues.patronymic,
    formValues.status?.value,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      setTouched({
        phone: true,
        status: true,
        firstName: true,
        lastName: true,
      });
    }
  }, [shouldValidate]);

  return (
    <Container>
      <Title>Add Lead</Title>
      <MainInfoContent>
        <InputGroup className="margin-none">
          <InputWrapper className="phone-select">
            <Label>
              Phone number <span>*</span>
            </Label>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <MyPhoneInput
                  value={field.value?.value || ''}
                  onChange={(phone) => field.onChange({ ...field.value, value: phone })}
                  onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
                />
              )}
            />
            {touched.phone && errors.phone && (
              <ErrorMessage className="phone-number">{errors.phone}</ErrorMessage>
            )}
          </InputWrapper>
          <InputWrapper>
            <Label>
              Status <span>*</span>
            </Label>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <CustomSelectComponent
                  label="Status"
                  req={true}
                  options={statusOptions}
                  value={field.value}
                  onChange={(option) => {
                    field.onChange(option);
                    setTouched((prev) => ({ ...prev, status: true }));
                  }}
                  onBlur={() => setTouched((prev) => ({ ...prev, status: true }))}
                  placeholder="Select status..."
                />
              )}
            />
            {touched.status && errors.status && <ErrorMessage>{errors.status}</ErrorMessage>}
          </InputWrapper>
        </InputGroup>
        <InputGroup>
          <InputWrapper className="name-input">
            <Label>
              First Name <span>*</span>
            </Label>
            <Controller
              control={control}
              name="firstName"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  placeholder="Full Name"
                  onBlur={() => setTouched((prev) => ({ ...prev, firstName: true }))}
                />
              )}
            />
            {touched.firstName && errors.firstName && (
              <ErrorMessage>{errors.firstName}</ErrorMessage>
            )}
          </InputWrapper>

          <InputWrapper className="name-input">
            <Label>
              Last Name <span>*</span>
            </Label>
            <Controller
              control={control}
              name="lastName"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  placeholder="Last Name"
                  onBlur={() => setTouched((prev) => ({ ...prev, lastName: true }))}
                />
              )}
            />
            {touched.lastName && errors.lastName && (
              <ErrorMessage>{errors.lastName}</ErrorMessage>
            )}
          </InputWrapper>
        </InputGroup>
        <InputGroup>
          <InputWrapper className="name-input patronymic">
            <Label>Patronymic</Label>
            <Controller
              control={control}
              name="patronymic"
              render={({ field }) => <Input {...field} type="text" placeholder="Patronymic" />}
            />
          </InputWrapper>
          <InputWrapper>
            <Label>Assignee</Label>
            <Controller
              control={control}
              name="assignee"
              render={({ field }) => {
                const userOptions = assignedList.map((user) => ({
                  value: user.uuid,
                  label: user.name,
                }));

                const noneOption = { value: 'none', label: 'none' };
                const options = [noneOption, ...userOptions];

                const currentValue =
                  field.value === null || field.value === 'none'
                    ? noneOption
                    : options.find((o) => o.value === field.value) || null;

                return (
                  <CustomSelectComponent
                    label="Assignee"
                    options={options}
                    value={currentValue}
                    onChange={(option) => {
                      const value = option?.value === 'none' ? null : option?.value;
                      field.onChange(value);
                    }}
                    onBlur={() => setTouched((prev) => ({ ...prev, assignee: true }))}
                    placeholder="Select assignee..."
                  />
                );
              }}
            />{' '}
          </InputWrapper>
        </InputGroup>
      </MainInfoContent>
    </Container>
  );
};

export default ContactInfo;
